import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Database, Heart, Mail, Instagram, Linkedin, Twitter } from 'lucide-react';
import { DataService } from '../services/dataService';
import { colleges } from '../data/colleges';
import { scholarships } from '../data/scholarships';
import { exams } from '../data/exams';
import { useUser } from '../context/UserContext';

export default function Footer() {
    const [seeding, setSeeding] = useState(false);
    const { realUser } = useUser();
    const isAdmin = realUser?.role === 'admin';

    const handleSeed = async () => {
        if (!window.confirm("Are you sure you want to seed the database? This will write initial colleges, scholarships and exams to Supabase.")) return;
        setSeeding(true);
        try {
            await DataService.seedDatabase(colleges, scholarships, exams || []);
            alert("Database seeded successfully!");
        } catch (error) {
            console.error("Seeding failed:", error);
            alert("Seeding failed. Check console.");
        } finally {
            setSeeding(false);
        }
    };

    return (
        <footer className="bg-slate-900 text-slate-400 py-12 border-t border-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                    {/* Brand Column */}
                    <div className="md:col-span-1">
                        <Link to="/" className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center font-bold text-white">E</div>
                            <span className="text-xl font-black text-white tracking-tight">EAGER</span>
                        </Link>
                        <p className="text-sm leading-relaxed mb-4">
                            India's first "Guidance Intelligence" platform. helping students find their path through Logic, not luck.
                        </p>
                        <div className="flex items-center gap-2 text-xs font-bold text-emerald-500 bg-emerald-500/10 px-3 py-1.5 rounded-full w-fit mb-4">
                            <Shield className="w-3 h-3" />
                            India Safe & Secure
                        </div>
                        {/* Social */}
                        <div className="flex items-center gap-2">
                            <a href="https://instagram.com/eager.education" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                                className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-pink-500 text-slate-400 hover:text-white flex items-center justify-center transition-all">
                                <Instagram className="w-4 h-4" />
                            </a>
                            <a href="https://linkedin.com/company/eager-education" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
                                className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-blue-500 text-slate-400 hover:text-white flex items-center justify-center transition-all">
                                <Linkedin className="w-4 h-4" />
                            </a>
                            <a href="https://twitter.com/eager_education" target="_blank" rel="noopener noreferrer" aria-label="Twitter"
                                className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-sky-500 text-slate-400 hover:text-white flex items-center justify-center transition-all">
                                <Twitter className="w-4 h-4" />
                            </a>
                            <a href="mailto:hello@eager.education" aria-label="Email"
                                className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-emerald-500 text-slate-400 hover:text-white flex items-center justify-center transition-all">
                                <Mail className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    {/* Company Column */}
                    <div>
                        <h4 className="text-white font-bold mb-4">Company</h4>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                            <li><Link to="/contact" className="hover:text-white transition-colors">Contact / Support</Link></li>
                        </ul>
                    </div>

                    {/* Legal Column */}
                    <div>
                        <h4 className="text-white font-bold mb-4">Legal & Trust</h4>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                            <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                            <li><Link to="/refunds" className="hover:text-white transition-colors">Refund Policy</Link></li>
                        </ul>
                    </div>

                    {/* Disclaimer Column */}
                    <div className="md:col-span-3">
                        <h4 className="text-white font-bold mb-2 text-xs uppercase tracking-widest">Disclaimer</h4>
                        <p className="text-xs leading-relaxed text-slate-500">
                            EAGER provides career guidance and information only. We do not guarantee admissions, jobs, or visas. All decisions taken based on our guidance are the user's responsibility.
                        </p>
                    </div>
                </div>

                <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-medium">
                    <div data-testid="footer-copyright">
                        &copy; 2026 Eager Educational Services. All rights reserved.
                    </div>

                    <div className="flex items-center gap-4">
                        {isAdmin && (
                            <button
                                onClick={handleSeed}
                                disabled={seeding}
                                data-testid="footer-seed-db"
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg transition-colors text-[10px] uppercase tracking-wider font-bold"
                                title="Admin only — seeds initial colleges, scholarships and exams into Supabase"
                            >
                                <Database className={`w-3 h-3 ${seeding ? 'animate-spin' : ''}`} />
                                {seeding ? 'Seeding…' : 'Admin: Seed DB'}
                            </button>
                        )}
                        <div className="flex items-center gap-1">
                            Made with <Heart className="w-3 h-3 text-rose-500 fill-rose-500" /> in Hyderabad, India.
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
